import { Localized, useLocalization } from '@fluent/react'
import { Alert, Button, Chip, Paper, TableContainer } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { useNavigate } from 'react-router-dom'
import { useAppSelector, useDataGridCustomLocaleText } from '../../../app/hooks'
import { QuickSearchToolbar } from '../../../app/QuickSearchToolbar'
import { FlexDiv } from '../../../app/Utils'
import { AddFabButton } from '../../../app/components/AddFabButton'
import { FormDefinitionStatus } from '../../documents/sections/formSection/formSchema'
import { selectFormDefinitions } from '../../documents/sections/formSection/formSectionSlice'
import { useFormDefinitions } from '../../documents/sections/formSection/formSectionLoaders'

export const FORM_DEFINITIONS_PATH = '/company/form-definitions'
export const FORM_DEFINITION_EDIT_PATH = (id: string) => `${FORM_DEFINITIONS_PATH}/edit/${id}`

export const FormDefinitionStatusChip = ({ status }: { status: FormDefinitionStatus }) => {
    const { l10n } = useLocalization()
    const color = status === 'PUBLISHED' ? 'success' : status === 'DRAFT' ? 'info' : 'default'
    return <Chip size="small" variant="outlined" color={color}
        label={l10n.getString(`form-def-status-${status.toLowerCase()}`, null, status)} />
}

export const FormDefinitionList = () => {
    const { l10n } = useLocalization()
    const navigate = useNavigate()
    const localeText = useDataGridCustomLocaleText()
    const definitions = useAppSelector(selectFormDefinitions)
    const loadState = useFormDefinitions()

    const rows = definitions.map(d => ({ ...d, fieldCount: d.fields.filter(f => f.type !== 'heading').length }))

    const columns: GridColDef[] = [
        { field: 'name', headerName: l10n.getString('form-def-name', null, 'Nazwa'), flex: 1, minWidth: 200 },
        {
            field: 'code', headerName: l10n.getString('form-def-code', null, 'Kod'), width: 200,
            renderCell: ({ value }) => <code>{value}</code>,
        },
        {
            field: 'status', headerName: l10n.getString('form-def-status', null, 'Status'), width: 150,
            renderCell: ({ value }) => <FormDefinitionStatusChip status={value} />,
        },
        {
            field: 'version', headerName: l10n.getString('form-def-version', null, 'Wersja'), width: 100,
            valueFormatter: value => (value ? `v${value}` : '—'),
        },
        { field: 'fieldCount', headerName: l10n.getString('form-def-field-count', null, 'Pola'), width: 100 },
        {
            field: 'actions', type: 'actions', width: 120,
            renderCell: ({ row }) => <Button onClick={() => navigate(FORM_DEFINITION_EDIT_PATH(row.id))} color="secondary" variant="outlined">
                <Localized id="go-to"><span>_go</span></Localized>
            </Button>,
        },
    ]

    if (loadState === 'error') {
        return <Alert severity="error">
            <Localized id="form-section-load-error"><span>_Nie udało się wczytać formularzy</span></Localized>
        </Alert>
    }

    return <TableContainer component={Paper}>
        <DataGrid
            loading={loadState === 'loading' && definitions.length === 0}
            style={{ minHeight: 100, height: 'calc(100vh - 250px)' }}
            localeText={localeText}
            rows={rows}
            columns={columns}
            slots={{ toolbar: QuickSearchToolbar }}
            onRowDoubleClick={({ row }) => navigate(FORM_DEFINITION_EDIT_PATH(row.id))} />
        <FlexDiv>
            <AddFabButton onClick={() => navigate(`${FORM_DEFINITIONS_PATH}/new`)} />
        </FlexDiv>
    </TableContainer>
}
